import { useLazyQuery } from '@apollo/client';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import Badge from '../components/Badge';
import Button from '../components/Button';
import CarSelector from '../components/CarSelector';
import ServiceSelector from '../components/ServiceSelector';
import styles from '../sass/components/Services.module.scss';
import {
  ROUTE_BOOK_ADD_ONS,
  ROUTE_BOOK_LOCATION,
  ROUTE_BOOK_SCHEDULE,
} from '../util/constants';
import { GET_USER_BY_ID } from '../util/gql';
import { clearAllAddOns } from '../features/addOns/addOnsSlice';
import usePrepareSchedule from '../hooks/usePrepareSchedule';
import { goBackTo } from '../util/stepNavigation';
import ServiceAddressSummary from '../components/ServiceAddressSummary';
import { isPlanBooking } from '../util/bookingPricing';
import { useTenant } from '../components/TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
// Imported last so the CSS bundle's module order is what it was before.
import BookingSummaryBar from '../components/BookingSummaryBar';

export default function Services() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Class hooks for a tenant theme preset's stylesheet (see
  // public/aperio/theme.css, "booking steps"). False for every tenant on the
  // default preset, so their class lists are unchanged.
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';
  const selectedServices = useAppSelector((state) => (
    state.serviceTypes.services
  ));
  const selectedCar = useAppSelector((state) => state.car.id);
  const customerId = useAppSelector((state) => state.customer.id);
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  const [loadUserInformation,
    { data: userInfo }] = useLazyQuery(GET_USER_BY_ID);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 1) {
      const userId = customerId || currentUser.id;
      loadUserInformation({ variables: { userId } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, currentUser]);

  // The franchise for the address and its travel charge (shared with the
  // consumer skin, hooks/usePrepareSchedule).
  const {
    prepare: prepareSchedule,
    loading: franchiseLoading,
  } = usePrepareSchedule();

  const handleBack = () => {
    goBackTo(navigate, ROUTE_BOOK_LOCATION);
  };

  // The Aperio plan offer's "Continue with the plan" puts the plan in the
  // basket, then asks to move on; the move waits for the basket to hold it so
  // handleNextClick reads the plan, not the empty basket it was clicked on.
  const [planContinueRequested, setPlanContinueRequested] = useState(false);

  const handleNextClick = async () => {
    // A plan booking is the onboarding visit: no optional add-ons, so the
    // add-ons step is skipped (mandatory fees are added on the schedule step).
    const planBooking = isPlanBooking(selectedServices);
    if (planBooking) dispatch(clearAllAddOns());
    try {
      await prepareSchedule();

      if (userInfo?.getUserById?.vendorId === 1 || planBooking) {
        navigate(ROUTE_BOOK_SCHEDULE);
      } else {
        navigate(ROUTE_BOOK_ADD_ONS);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error finding potential franchise', error);
    }
  };

  useEffect(() => {
    if (!planContinueRequested) return;
    if (!isPlanBooking(selectedServices) || selectedCar === null) return;
    setPlanContinueRequested(false);
    handleNextClick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planContinueRequested, selectedServices, selectedCar]);

  return (
    <div className={cx(styles.services, { 'apr-step': themed })}>
      <div className={styles.header}>
        <h1 className={styles.headerDesktop}>{t('services.title')}</h1>
        {/* The phone-sized copy of the h1 (QA 2026-09-25 #29). */}
        <h4 className={styles.headerMobile} aria-level={1}>
          {t('services.title')}
        </h4>
      </div>
      <div
        className={cx(styles.body, 'container--fluid', {
          'apr-step__body': themed,
        })}
      >
        <div className={cx(styles.bodyLeft, 'container__col-xs-4')}>
          {themed && <ServiceAddressSummary />}
          <div className={styles.carSelectorBox}>
            <CarSelector />
          </div>
        </div>
        <div className={cx(
          styles.bodyRight,
          'container__col-xs-8',
        )}
        >
          <ServiceSelector
            type="primary"
            onPlanContinue={() => setPlanContinueRequested(true)}
          />
        </div>
      </div>
      <div className={cx(styles.footer, { 'apr-step__footer': themed })}>
        {themed ? (
          // The price strip rides in this bar under the preset, so only one
          // bar is pinned (see BookingSummaryBar `placement="footer"`).
          <BookingSummaryBar placement="footer" />
        ) : (
          <Badge
            variant="secondary"
            className={styles.countBadge}
          >
            {t('services.onlyOne')}
          </Badge>
        )}
        <Button
          className={cx(styles.editButton, { 'apr-step__back': themed })}
          onClick={handleBack}
          variant="tertiary"
        >
          {t('common.back')}
        </Button>
        <Button
          className={styles.editButton}
          variant="primary"
          onClick={handleNextClick}
          inactive={
            selectedServices.length < 1 || selectedCar === null
          }
          loading={franchiseLoading}
        >
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}
